import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SavesContext = createContext();

export const useSaves = () => {
  const context = useContext(SavesContext);
  if (!context) {
    throw new Error('useSaves must be used within a SavesProvider');
  }
  return context;
};

export const SavesProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [savedItems, setSavedItems] = useState([]);

  // Load saves from localStorage when user changes or authenticates
  useEffect(() => {
    const loadSaves = () => {
      if (!isAuthenticated || !user) {
        setSavedItems([]);
        return;
      }

      const key = `fozzil_saves_${user.id}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          setSavedItems(JSON.parse(stored));
        } catch (e) {
          console.error('Error parsing saved items:', e);
          setSavedItems([]);
        }
      } else {
        setSavedItems([]);
      }
    };

    loadSaves();
  }, [user, isAuthenticated]);

  // Persist saves to localStorage
  useEffect(() => {
    if (isAuthenticated && user) {
      const key = `fozzil_saves_${user.id}`;
      localStorage.setItem(key, JSON.stringify(savedItems));
    }
  }, [savedItems, user, isAuthenticated]);

  const toggleSave = (product) => {
    if (!isAuthenticated) return false;

    setSavedItems(prev => {
      const isSaved = prev.some(item => item.id === product.id);
      if (isSaved) {
        return prev.filter(item => item.id !== product.id);
      } else {
        return [...prev, { ...product, savedAt: new Date().toISOString() }];
      }
    });
    return true;
  };

  const isSaved = (productId) => {
    return savedItems.some(item => item.id === productId);
  };

  const removeSave = (productId) => {
    setSavedItems(prev => prev.filter(item => item.id !== productId));
  };

  const clearSaves = () => {
    setSavedItems([]);
  };

  const value = {
    savedItems,
    toggleSave,
    isSaved,
    removeSave,
    clearSaves,
  };

  return (
    <SavesContext.Provider value={value}>
      {children}
    </SavesContext.Provider>
  );
};
